/* eslint-disable react/prop-types */
import React from 'react'
import { Col, Row } from 'react-bootstrap'
import {
  formatMfaConfigurationColumn,
  formatMfaStatusType,
  getAuthenticationTypeFormatter,
} from '../../../../../util/table.util'

import StatusBadge from '../../../../../components/StatusBadge'
import DetailRow from '../../../../../components/DetailRow'
import Pill from '../../../../../components/Pill'
import { getEmployeeLicenseDisplayNames } from '../../../../../util/employeeLicenses.util'
import { GWS } from '../../../engage/utils'
import EmployeeActionToolbar from '../EmployeeActionToolbar'
import MailboxDelegations from '../MailboxDelegations'

/** Known MFA status values from API */
const MFA_STATUS_KNOWN = ['PROTECTED', 'SIGN_IN_BLOCKED', 'BREAK_GLASS', 'NOT_PROTECTED', 'UNKNOWN', 'LOCKED_OUT']

/** Label for MFA status; uses INTEGRATION.* i18n when known, otherwise "Unknown". */
const getMfaStatusLabel = (status, intl) => {
  if (!status) return intl.formatMessage({ id: 'INTEGRATION.UNKNOWN' })
  if (MFA_STATUS_KNOWN.includes(status)) return intl.formatMessage({ id: `INTEGRATION.${status}` })
  return intl.formatMessage({ id: 'INTEGRATION.UNKNOWN' })
}

/** Registration from mfaStatus.statusList only */
const getMfaRegistrationDisplay = (employee, intl) => {
  const list = employee?.mfaStatus?.statusList
  if (list?.length) {
    return list
      .map(
        (i) =>
          `${formatMfaStatusType(i.type, intl)}${i.status ? ` - ${intl.formatMessage({ id: `GENERAL.${i.status}` })}` : ''}`,
      )
      .join(', ')
  }
  return 'None'
}

/** Authentication methods: actual registered methods (e.g. Phone, Authenticator), same pattern as Reports → MFA Report. */
const getAuthMethodsDisplay = (employee, intl) => {
  const methods = employee?.authenticationType
  if (methods?.length) {
    const format = getAuthenticationTypeFormatter(intl)
    return methods.map(format).join(', ')
  }
  return 'None'
}

function Overview(props) {
  const {
    employee,
    selectedCustomer,
    onEdit,
    intl,
    overrideEngageLicensing = false,
    canPerformActions = true,
    setAction,
    setModal,
    openIntegrationModal,
    selectedIntegration,
  } = props

  const isGws = selectedIntegration?.IntegrationApplication?.application_name === GWS
  const hasUserMailbox = employee?.isUserMailBoxAvailable === true

  return (
    <>
      <EmployeeActionToolbar
        employee={employee}
        selectedCustomer={selectedCustomer}
        onEdit={onEdit}
        canPerformActions={canPerformActions}
        overrideEngageLicensing={overrideEngageLicensing}
        setAction={setAction}
        setModal={setModal}
        openIntegrationModal={openIntegrationModal}
        selectedIntegration={selectedIntegration}
      />

      <div className="section px-4 py-4">
        {/* Account Configuration Section */}
        <Row className="mb-4">
          <Col sm="12">
            <h6 className="text-bold mb-3" style={{ fontSize: '1rem', color: 'var(--bs-body-color)' }}>
              Account Configuration
            </h6>
            <DetailRow
              label="User type"
              value={
                <Pill variant="secondary" size="medium" withMargins={false}>
                  {employee.userType || 'Member'}
                </Pill>
              }
              size="sm"
            />

            <DetailRow
              label="Sign-in status"
              value={
                employee.isUserSigninEnabled === false || employee.enabled === false ? (
                  <StatusBadge variant="danger">Blocked sign-in</StatusBadge>
                ) : (
                  <StatusBadge variant="success">Allowed</StatusBadge>
                )
              }
              size="sm"
            />

            <DetailRow
              label="Roles"
              value={
                employee.role && employee.role.length > 0
                  ? employee.role.map((role) => (
                      <Pill key={role} variant="secondary" size="medium" withMargins={false} className="me-1 mb-1">
                        {role}
                      </Pill>
                    ))
                  : 'User'
              }
              size="sm"
            />

            <DetailRow
              label="Groups"
              value={(() => {
                // Flatten groupsByType into a single array
                const allGroups = employee.groupsByType ? Object.values(employee.groupsByType).flat() : []
                return allGroups.length > 0 ? allGroups.map((group) => group.name).join(', ') : ''
              })()}
              size="sm"
            />

            <DetailRow
              label="Licenses"
              value={getEmployeeLicenseDisplayNames(
                employee,
                selectedIntegration?.IntegrationApplication?.application_name,
              ).join(', ')}
              size="sm"
            />

            <DetailRow
              label="Mailbox rules"
              value={
                employee?.forwardingSmtpAddress || employee?.forwarding_smtp_address ? (
                  <StatusBadge variant="warning">Mail forwarding enabled</StatusBadge>
                ) : (
                  ''
                )
              }
              size="sm"
            />

            <DetailRow
              label="Created"
              value={
                employee.createdDateTime
                  ? intl.formatDate(employee.createdDateTime, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZoneName: 'short',
                    })
                  : 'None'
              }
              size="sm"
            />
          </Col>
        </Row>

        <MailboxDelegations
          customerId={selectedCustomer?.id}
          employeeId={employee?.id ?? employee?.employeeId}
          enabled={hasUserMailbox && !isGws}
          canManage={canPerformActions}
        />

        {/* Multifactor Authentication Section */}
        <Row
          className="mb-4"
          style={{ borderTop: '1px solid var(--aug-ui-divider-subtle)', paddingTop: '1.5rem', marginTop: '1.5rem' }}
        >
          <Col sm="12">
            <h6 className="text-bold mb-3" style={{ fontSize: '1rem', color: 'var(--bs-body-color)' }}>
              Multifactor Authentication
            </h6>
            <DetailRow
              label="Status"
              value={
                <span className="d-inline-block" style={{ overflow: 'hidden' }}>
                  <div
                    className={
                      employee?.mfaStatus?.status && MFA_STATUS_KNOWN.includes(employee.mfaStatus.status)
                        ? `duo${employee.mfaStatus.status}`
                        : 'duoUNKNOWN'
                    }
                  />
                  {getMfaStatusLabel(employee?.mfaStatus?.status ?? null, intl)}
                </span>
              }
              size="sm"
            />

            <DetailRow label="Registration" value={getMfaRegistrationDisplay(employee, intl)} size="sm" />

            <DetailRow label="Authentication methods" value={getAuthMethodsDisplay(employee, intl)} size="sm" />

            <DetailRow
              label="MFA Configuration"
              value={
                employee?.mfaConfigurations != null ? formatMfaConfigurationColumn(null, employee, null, intl) : 'None'
              }
              size="sm"
            />
          </Col>
        </Row>

        {/* Recent Activity Section */}
        <Row
          className="mb-4"
          style={{ borderTop: '1px solid var(--aug-ui-divider-subtle)', paddingTop: '1.5rem', marginTop: '1.5rem' }}
        >
          <Col sm="12">
            <h6 className="text-bold mb-3" style={{ fontSize: '1rem', color: 'var(--bs-body-color)' }}>
              Recent Activity
            </h6>

            <DetailRow
              label="Last password change"
              value={
                employee.lastPasswordChangeDateTime
                  ? intl.formatDate(employee.lastPasswordChangeDateTime, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZoneName: 'short',
                    })
                  : 'None'
              }
              size="sm"
            />

            <DetailRow
              label="Sign-in sessions valid from date"
              value={
                employee.refreshTokensValidFromDateTime
                  ? intl.formatDate(employee.refreshTokensValidFromDateTime, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZoneName: 'short',
                    })
                  : 'None'
              }
              size="sm"
            />
          </Col>
        </Row>
      </div>
    </>
  )
}

export default Overview
